import React from 'react';
import { createRoot, hydrateRoot, Root } from 'react-dom/client';
import ObserverWidget from './ObserverWidget';
import { logDiff } from './clientLogger';
import {
    GenerateComponentArgs,
    getComponentByType,
    getWrapperId,
    populateWidgetStore,
} from './utils';

const rootsByWidgetId = new Map<string, { root: Root; container: Element }>();

// Hydrating a widget again replaces its root: two roots on one container remove each other's nodes.
function releaseRoot(widgetId: string): void {
    const entry = rootsByWidgetId.get(widgetId);
    if (!entry) {
        return;
    }
    rootsByWidgetId.delete(widgetId);
    const markup = entry.container.innerHTML;
    try {
        entry.root.unmount();
    } catch {
        // throws when the editor already patched the DOM; React still runs the cleanups
    }
    entry.container.innerHTML = markup;
}

/**
 * This is being called only on the client side, to hydrate the widget
 */
export function hydrateComponent(
    { type, props, id, observer }: GenerateComponentArgs,
    { markupId = id }: { markupId?: string }
): void {
    props._styles = props?._styles || {};
    const Component = getComponentByType(type);
    if (Component && id && markupId && document.getElementById(id)) {
        releaseRoot(id);
        if (observer) {
            populateWidgetStore(id as string, props, type);
        }
        const reactResult = observer ? (
            <ObserverWidget Component={Component} widgetId={id} />
        ) : (
            <Component {...props} widgetId={id} />
        );
        const container = document.getElementById(getWrapperId(id));
        if (container) {
            const preHydration = container.outerHTML;
            const root = hydrateRoot(container, reactResult, {
                onRecoverableError: (error, errorInfo) =>
                    logDiff(preHydration, error, errorInfo, type, id),
            });
            rootsByWidgetId.set(id, { root, container });
        }
    }
}

/**
 * @todo
 * TODO: Remove flow after completing change to align to server render on adding new widget.
 * This can be used to client-side render a new widget (without SSR)
 */
export const renderObserverComponent = ({
    type,
    props,
    id,
}: GenerateComponentArgs): void | string | null => {
    const Component = getComponentByType(type);
    if (Component) {
        if (id && document.getElementById(id)) {
            releaseRoot(id);
            // Populate the propsStore with the received props
            populateWidgetStore(id as string, props, type);
            const container = document.getElementById(id)!;
            const root = createRoot(container);
            rootsByWidgetId.set(id, { root, container });
            root.render(<ObserverWidget Component={Component} widgetId={id} />);
        }
    }
};
